import React, { useState, useEffect, useRef } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Input } from './ui/input';
import { MapPin, X } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Inner component that uses the Places library
function PlacesAutocompleteInput({ value, onChange, placeholder }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const places = useMapsLibrary('places');
  const autocompleteService = useRef(null);

  useEffect(() => {
    if (!places) return;
    autocompleteService.current = new places.AutocompleteService();
  }, [places]);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleInputChange = async (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (!newValue.trim()) {
      setPredictions([]);
      setShowDropdown(false);
      onChange('');
      return;
    }

    if (!autocompleteService.current) return;

    try {
      const request = {
        input: newValue,
        componentRestrictions: { country: 'lk' },
        types: ['(cities)'], // Only show cities
      };

      autocompleteService.current.getPlacePredictions(request, (results, status) => {
        if (status === 'OK' && results) {
          setPredictions(results);
          setShowDropdown(true);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      });
    } catch (error) {
      console.error('Autocomplete error:', error);
    }
  };

  const handleSelectPrediction = (prediction) => {
    const cityName = prediction.structured_formatting.main_text;
    setInputValue(cityName);
    onChange(cityName);
    setPredictions([]);
    setShowDropdown(false);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    setPredictions([]);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (predictions.length > 0) {
              setShowDropdown(true);
            }
          }}
          className="pr-20"
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Predictions Dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelectPrediction(prediction)}
              className="w-full text-left px-4 py-3 hover:bg-gray-100 border-b last:border-b-0 flex items-start gap-2 transition-colors"
            >
              <MapPin className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {prediction.structured_formatting.main_text}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {prediction.structured_formatting.secondary_text}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Wrapper component with APIProvider
const CityAutocomplete = ({ value, onChange, placeholder = "Type nearest city" }) => {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <PlacesAutocompleteInput 
        value={value} 
        onChange={onChange} 
        placeholder={placeholder}
      />
    </APIProvider>
  );
};

export default CityAutocomplete;
